import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import api from "../../../api/axiosInstance";
import Logo from "../../../components/Logo";
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from "amazon-chime-sdk-js";
import { FaVideo, FaVideoSlash } from "react-icons/fa";
import { BsFillMicFill, BsFillMicMuteFill } from "react-icons/bs";
import { MdOutlineCameraswitch } from "react-icons/md";

const JoinMeeting = () => {
  const { t } = useTranslation();
  const { meetingId } = useParams();
  const [data, setData] = useState({});
  const apiUrl = import.meta.env.VITE_API_URL;
  const contentUrl = import.meta.env.VITE_CONTENT_URL;

  const audioRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteContainerRef = useRef(null);

  const meetingSessionRef = useRef(null);
  const audioBoundRef = useRef(false);
  const audioVideoObserverRef = useRef(null);

  const videoDevicesRef = useRef([]);
  const currentCameraIndexRef = useRef(0);

  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(true);

  function tryBindAudio() {
    const meetingSession = meetingSessionRef.current;
    const audioEl = audioRef.current;

    if (!meetingSession || !audioEl || audioBoundRef.current) return;

    meetingSession.audioVideo.bindAudioElement(audioEl);
    audioBoundRef.current = true;
  }

  const switchCamera = async () => {
    const meetingSession = meetingSessionRef.current;
    if (!meetingSession) return;

    const audioVideo = meetingSession.audioVideo;

    const devices = videoDevicesRef.current;

    if (!devices || devices.length <= 1) return;

    const next =
      (currentCameraIndexRef.current + 1) % devices.length;

    await audioVideo.startVideoInput(devices[next].deviceId);

    currentCameraIndexRef.current = next;
  };

  useEffect(() => {
    const fetchData = async () => {
      api
        .get(apiUrl + "/api/v1/meetings/join/details/" + meetingId)
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error: unable to retrieve information | ", error);
        });
    };

    fetchData();
  }, []);

  const joinMeeting = async () => {
    if (joining || joined) return;
    setJoining(true);

    try {
      const response = await api.post(
        `${apiUrl}/api/v1/meetings/join/attendee/${meetingId}`,
      );

      const logger = new ConsoleLogger("Chime", LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger);

      const configuration = new MeetingSessionConfiguration(
        response.data.meeting,
        response.data.attendee,
      );

      const meetingSession = new DefaultMeetingSession(
        configuration,
        logger,
        deviceController,
      );

      meetingSessionRef.current = meetingSession;
      const audioVideo = meetingSession.audioVideo;

      const audioInputs = await audioVideo.listAudioInputDevices();
      const videoInputs = await audioVideo.listVideoInputDevices();

      videoDevicesRef.current = videoInputs;
      currentCameraIndexRef.current = 0;

      if (audioInputs.length) {
        await audioVideo.startAudioInput(audioInputs[0].deviceId);
      }
      if (videoInputs.length) {
        await audioVideo.startVideoInput(videoInputs[0].deviceId);
      }

      const observer = {
        videoTileDidUpdate: (tileState) => {
          if (!tileState.boundAttendeeId) return;

          // Local video
          if (tileState.localTile) {
            if (localVideoRef.current) {
              audioVideo.bindVideoElement(
                tileState.tileId,
                localVideoRef.current,
              );
            }
            return;
          }

          // Prevent duplicate tiles
          const existingVideo = remoteContainerRef.current?.querySelector(
            `video[data-tile-id="${tileState.tileId}"]`,
          );

          if (existingVideo) {
            // Just rebind, DO NOT create new element
            audioVideo.bindVideoElement(tileState.tileId, existingVideo);
            return;
          }

          // Create video ONLY ONCE
          const video = document.createElement("video");
          video.autoplay = true;
          video.playsInline = true;
          video.dataset.tileId = tileState.tileId;
          video.className = "w-full h-full object-cover rounded-xl bg-black";

          audioVideo.bindVideoElement(tileState.tileId, video);
          remoteContainerRef.current?.appendChild(video);
        },

        videoTileWasRemoved: (tileId) => {
          const video = remoteContainerRef.current?.querySelector(
            `video[data-tile-id="${tileId}"]`,
          );
          video?.remove();
        },
      };

      audioVideoObserverRef.current = observer;
      audioVideo.addObserver(observer);

      // Start session
      audioVideo.start();
      audioVideo.startLocalVideoTile();

      setJoined(true);
      tryBindAudio();
    } catch (err) {
      console.error("Failed to join meeting", err);
    } finally {
      setJoining(false);
    }
  };

  /* ---------------------------------------------
     LEAVE MEETING
  --------------------------------------------- */
  async function leaveMeeting() {
    const meetingSession = meetingSessionRef.current;
    if (!meetingSession) return;

    if (!confirm("Aye you sure, you want to leave this meeting?")) {
      return;
    }

    const audioVideo = meetingSession.audioVideo;

    try {
      audioVideo.stopLocalVideoTile();

      await audioVideo.stopVideoInput();

      await audioVideo.stopAudioInput();

      audioVideo.stop();

      // Remove observer
      if (audioVideoObserverRef.current) {
        audioVideo.removeObserver(audioVideoObserverRef.current);
        audioVideoObserverRef.current = null;
      }

      // Cleanup DOM
      remoteContainerRef.current.innerHTML = "";

      // Reset refs
      meetingSessionRef.current = null;
      audioBoundRef.current = false;

      // Reset UI state
      setJoined(false);
      setMuted(false);
      setVideoOn(true);

      // authorizedApi
      //   .post(apiUrl + "/api/v1/meetings/end?meetingId=" + meetingId)
      //   .then((response) => {
      //     console.log(response.data.message);
      //   })
      //   .catch((error) => {
      //     toast.error(error.response.data.message);
      //   });
    } catch (err) {
      console.error("Error while leaving meeting:", err);
    }
  }

  /* ---------------------------------------------
     CONTROLS
  --------------------------------------------- */
  function toggleMute() {
    const audioVideo = meetingSessionRef.current?.audioVideo;
    if (!audioVideo) return;

    muted
      ? audioVideo.realtimeUnmuteLocalAudio()
      : audioVideo.realtimeMuteLocalAudio();

    setMuted(!muted);
  }

  async function toggleVideo() {
    const audioVideo = meetingSessionRef.current?.audioVideo;
    if (!audioVideo) return;

    if (videoOn) {
      audioVideo.stopLocalVideoTile();
      await audioVideo.stopVideoInput();
    } else {
      const devices = await audioVideo.listVideoInputDevices();
      if (!devices.length) return;

      await audioVideo.startVideoInput(devices[0].deviceId);
      audioVideo.startLocalVideoTile();
    }

    setVideoOn(v => !v);
  }

  /* ---------------------------------------------
     SAFE AUDIO BIND AFTER EVERY RENDER
  --------------------------------------------- */
  useEffect(() => {
    tryBindAudio();
  });

  /* ---------------------------------------------
     CLEANUP ON UNMOUNT
  --------------------------------------------- */
  useEffect(() => {
    return () => leaveMeeting();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t("pages.meeting.join.title")}</title>
      </Helmet>

      <div className="flex flex-col flex-1 w-full">
        <Logo />

        <div className="w-full max-w-6xl mx-auto mt-6">
          <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex-auto p-5">
                <div className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800">
                  <img src={contentUrl + data.imageUrl} alt="user" />
                </div>

                <h4 className="mt-4 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
                  {data.firstName} {data.lastName}
                </h4>

                <p className="my-2 text-gray-500 dark:text-gray-400">
                  {data.idNumber}
                </p>

                <p className="my-2 text-gray-500 dark:text-gray-400">
                  {data.email}
                </p>

                <p className="my-2 text-gray-500 dark:text-gray-400">
                  {data.mobile}
                </p>
              </div>

              <div className="flex-auto">
                {!joined && (
                  <dov className="p-5 align-left">
                    <button
                      type="button"
                      onClick={joinMeeting}
                      disabled={joining}
                      className="px-4 py-2 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                    >
                      {joining ? "Starting ..." : "Join Video Call"}
                    </button>
                  </dov>
                )}

                {joined && (
                  <div className="border p-10 items-center border-gray-200 rounded-2xl dark:border-gray-800">
                    <div className="w-full h-[400px] bg-neutral-900 text-white flex flex-col">
                      <audio ref={audioRef} autoPlay />

                      <div className="flex-1 relative bg-neutral-900">
                        {/* Remote videos (full area) */}
                        <div
                          ref={remoteContainerRef}
                          className="absolute inset-0 p-4"
                        />

                        {/* Local video (PiP bottom-right) */}
                        {videoOn && (
                          <div className="absolute bottom-4 right-4 w-38 h-26 md:w-46 md:h-30 bg-black rounded-xl overflow-hidden shadow-2xl border border-neutral-700">
                            <video
                              ref={localVideoRef}
                              autoPlay
                              muted
                              playsInline
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </div>

                      {/* Controls */}
                      <div className="flex justify-center gap-4 p-4 bg-neutral-800">
                        <button
                          onClick={toggleMute}
                          className="px-5 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600"
                        >
                          {muted ? <BsFillMicMuteFill /> : <BsFillMicFill />}
                        </button>

                        <button
                          onClick={toggleVideo}
                          className="px-5 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600"
                        >
                          {videoOn ? <FaVideo /> : <FaVideoSlash />}
                        </button>

                        <button
                          onClick={switchCamera}
                          className="px-5 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600"
                        >
                          <MdOutlineCameraswitch />
                        </button>

                        <button
                          onClick={leaveMeeting}
                          className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-500"
                        >
                          Leave
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
                <div className="order-3 xl:order-2">
                  
                  <div className="flex flex-col items-center gap-1 text-center xl:flex-row xl:gap-3 xl:text-left">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {data.email}
                    </p>
                    <div className="hidden h-3.5 w-px bg-gray-300 dark:bg-gray-700 xl:block"></div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {data.mobile}
                    </p>
                  </div>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default JoinMeeting;
